import React, { useMemo } from 'react';

import type AnalyticsWebClient from '@atlassiansox/analytics-web-client/client';

import type { GasPurePayload, GasPureScreenEventPayload } from '@atlaskit/analytics-gas-types';
import FabricAnalyticsListeners from '@atlaskit/analytics-listeners/FabricAnalyticsListeners';
import type { AnalyticsWebClient as FabricAnalyticsWebClient } from '@atlaskit/analytics-listeners/types';

const DEFAULT_SOURCE = 'teamworkGraph';

/**
 * Adapts @atlassiansox/analytics-web-client to the @atlaskit/analytics-listeners
 * AnalyticsWebClient type. The two packages define AnalyticsWebClient with different
 * method signatures — the SOX version requires `action` and `source` fields that
 * may not be present in the GasPurePayload type. This adapter fills in defaults.
 */
const useAnalyticsClientAdapter = (client: AnalyticsWebClient): FabricAnalyticsWebClient =>
	useMemo(
		() => ({
			sendUIEvent: (event: GasPurePayload) =>
				client.sendUIEvent({
					...event,
					action: event.action || '',
					source: event.source || DEFAULT_SOURCE,
				}),
			sendOperationalEvent: (event: GasPurePayload) =>
				client.sendOperationalEvent({
					...event,
					action: event.action || '',
					source: event.source || DEFAULT_SOURCE,
				}),
			sendTrackEvent: (event: GasPurePayload) =>
				client.sendTrackEvent({
					...event,
					action: event.action || '',
					source: event.source || DEFAULT_SOURCE,
				}),
			sendScreenEvent: (event: GasPureScreenEventPayload) => client.sendScreenEvent(event),
		}),
		[client],
	);

type AnalyticsProviderProps = {
	analyticsClient: AnalyticsWebClient;
	children: React.ReactNode;
};

/**
 * Wraps the app with FabricAnalyticsListeners to capture analytics events
 * fired via @atlaskit/analytics-next and route them to the AnalyticsWebClient.
 *
 * FabricAnalyticsListeners listens on all Fabric/Atlaskit channels (including
 * 'teamworkGraph') and forwards events to the adapted analytics client.
 */
export const AnalyticsProvider = ({
	children,
	analyticsClient,
}: AnalyticsProviderProps): JSX.Element => {
	return (
		<FabricAnalyticsListeners client={useAnalyticsClientAdapter(analyticsClient)}>
			{children}
		</FabricAnalyticsListeners>
	);
};
